import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import type { FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import {
  securityGroupingUpdateService,
  SECURITY_GROUPING_LIST_REFRESH_EVENT,
  type SecurityGroupItem,
} from '@/api/domains/securityGrouping';
import { injectValidationErrors } from '@/utils/formValidationErrors';
import { formatToDdMmYyyy } from '@/utils';
import {
  editSecurityGroupingFormSchema,
  type EditSecurityGroupingFormValues,
} from '@/schemas/securityGrouping/editSecurityGroupingForm';


export interface UseEditSecurityGroupingFormParams {
  groupId: number;
  initialData: SecurityGroupItem;
  onSubmit?: () => void;
}

export interface EditSecurityGroupingFormMetadata {
  id: number;
  createdDate: string;
  createdBy: string;
  modifiedDate: string;
  modifiedBy: string;
  isRexActive: boolean;
  itemRex: string;
}

export interface UseEditSecurityGroupingFormResult {
  codeValue: string;
  groupNameValue: string;
  sendsAttendanceStatusValue: boolean;
  handleCodeChange: (value: string) => void;
  handleGroupNameChange: (value: string) => void;
  handleSendsAttendanceStatusChange: (checked: boolean) => void;
  handleSubmit: (event?: FormEvent<HTMLFormElement>) => void;
  isSubmitting: boolean;
  isReady: boolean;
  errors: FieldErrors<EditSecurityGroupingFormValues>;
  isSubmitted: boolean;
  metadata: EditSecurityGroupingFormMetadata;
}

export function useEditSecurityGroupingForm({
  groupId,
  initialData,
  onSubmit,
}: UseEditSecurityGroupingFormParams): UseEditSecurityGroupingFormResult {
  const { t: translate } = useTranslation();
  const { showNotification, showApiError, closeSidePanel } = useUI();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    handleSubmit: rhfHandleSubmit,
    setValue,
    setError,
    watch,
    formState,
  } = useForm<EditSecurityGroupingFormValues>({
    resolver: zodResolver(editSecurityGroupingFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      code: '',
      groupName: '',
      sendsAttendanceStatus: false,
    },
  });

  useEffect(() => {
    setValue('code', initialData.code);
    setValue('groupName', initialData.name);
    setValue('sendsAttendanceStatus', initialData.sendsAttendanceStatus);
  }, [initialData, setValue]);

  const codeValue = watch('code') || '';
  const groupNameValue = watch('groupName') || '';
  const sendsAttendanceStatusValue = watch('sendsAttendanceStatus') ?? false;

  const isReady = useMemo(
    () => groupNameValue.trim().length > 0,
    [groupNameValue],
  );

  const metadata: EditSecurityGroupingFormMetadata = useMemo(
    () => ({
      id: initialData.id,
      createdDate: formatToDdMmYyyy(initialData.createdDate) || '-',
      createdBy: initialData.createdBy || '-',
      modifiedDate: initialData.modifiedDate ? (formatToDdMmYyyy(initialData.modifiedDate) || '-') : '-',
      modifiedBy: initialData.modifiedBy || '-',
      isRexActive: initialData.isRexActive,
      itemRex: initialData.idRex || '-',
    }),
    [initialData],
  );

  const handleCodeChange = useCallback((value: string) => {
    setValue('code', value, { shouldValidate: true, shouldDirty: true });
  }, [setValue]);

  const handleGroupNameChange = useCallback((value: string) => {
    setValue('groupName', value, { shouldValidate: true, shouldDirty: true });
  }, [setValue]);

  const handleSendsAttendanceStatusChange = useCallback((checked: boolean) => {
    setValue('sendsAttendanceStatus', checked, { shouldValidate: true, shouldDirty: true });
  }, [setValue]);

  const onValid = useCallback(
    async (data: EditSecurityGroupingFormValues) => {
      setIsSubmitting(true);

      try {
        const response = await securityGroupingUpdateService.update(groupId, {
          code: data.code,
          groupName: data.groupName,
          sendsAttendanceStatus: data.sendsAttendanceStatus,
        });

        if (response.success) {
          showNotification('success', translate('securityGrouping:messages.updateSuccess'));
          window.dispatchEvent(new CustomEvent(SECURITY_GROUPING_LIST_REFRESH_EVENT));
          closeSidePanel();
          onSubmit?.();
        } else if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
          injectValidationErrors(response.error.validationErrors, { setError });
        } else {
          showApiError(response, translate('common:errors.unexpected'));
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [groupId, showNotification, showApiError, translate, closeSidePanel, setError, onSubmit],
  );

  const handleSubmit = useCallback(
    (event?: FormEvent<HTMLFormElement>) => {
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
        event.stopPropagation();
      }

      const wrappedValid = (data: EditSecurityGroupingFormValues) => { void onValid(data); };
      const runner = rhfHandleSubmit(wrappedValid, () => {});
      void runner(event);
    },
    [rhfHandleSubmit, onValid],
  );

  return {
    codeValue,
    groupNameValue,
    sendsAttendanceStatusValue,
    handleCodeChange,
    handleGroupNameChange,
    handleSendsAttendanceStatusChange,
    handleSubmit,
    isSubmitting,
    isReady,
    errors: formState.errors,
    isSubmitted: formState.isSubmitted,
    metadata,
  };
}
